import React, { useMemo } from "react";
import { Routes, Route, Link, NavLink, useParams, useLocation } from "react-router-dom";
import {
  SECTIONS, REGISTERS, STATUS, AUDIT_DATE, entryById, statusCounts, allConcerns,
  dataSourceRegister, calculationRegister, mockSeededRegister, CHANGE_LOG, ALL_ENTRIES, GLOBAL_NOTES,
} from "@/pages/docs/docsData";
import FeedbackAdmin from "@/pages/docs/FeedbackAdmin";
import FeedbackTicket from "@/pages/docs/FeedbackTicket";

const COL = {
  emerald: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800",
  purple: "bg-purple-100 text-purple-800", rose: "bg-rose-100 text-rose-800", slate: "bg-slate-200 text-slate-700",
};
const SEV = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.static;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold tracking-wide ${COL[s.colour]}`} data-testid={`status-${status}`}>{s.chip}</span>;
}
function Sev({ level }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${SEV[level] || SEV.low}`}>{level}</span>;
}

function Sidebar() {
  const linkCls = ({ isActive }) => `block px-3 py-1.5 rounded-md text-[13px] ${isActive ? "bg-slate-900 text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <aside className="w-64 shrink-0 border-r border-slate-200 h-screen sticky top-0 overflow-y-auto bg-white">
      <div className="px-5 h-14 flex items-center gap-2 border-b border-slate-100">
        <span className="font-bold text-slate-900">HTSS Ops</span>
        <span className="text-xs text-slate-400">Internal Docs</span>
      </div>
      <nav className="p-3 space-y-4">
        <div>
          <NavLink to="/docs" end className={linkCls} data-testid="docs-nav-overview">Overview</NavLink>
          <NavLink to="/docs/feedback" className={linkCls} data-testid="docs-nav-feedback">Feedback &amp; Bugs</NavLink>
        </div>
        {SECTIONS.map((s) => (
          <div key={s.key}>
            <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
            {s.entries.map((e) => (
              <NavLink key={e.id} to={`/docs/${e.id}`} className={linkCls} data-testid={`docs-nav-${e.id}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${COL[STATUS[e.status].colour].split(" ")[0]}`} />{e.title}
                </span>
              </NavLink>
            ))}
          </div>
        ))}
        <div>
          <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Registers</p>
          {REGISTERS.map((r) => (
            <NavLink key={r.key} to={`/docs/registers/${r.key}`} className={linkCls} data-testid={`docs-nav-reg-${r.key}`}>{r.label}</NavLink>
          ))}
        </div>
      </nav>
    </aside>
  );
}

function H2({ children }) { return <h2 className="text-lg font-bold text-slate-900 border-b border-slate-200 pb-2 mt-8 mb-4">{children}</h2>; }

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100">
          {head.map((h) => <th key={h} className="py-2 px-3 font-semibold">{h}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-slate-50">{rows}</tbody>
      </table>
    </div>
  );
}

function DocsHome() {
  const c = statusCounts();
  const concerns = allConcerns();
  const cards = [
    ["Total documented", c.total, "slate"], ["Live & wired", c.live, "emerald"],
    ["Partial / attention", c.partial, "amber"], ["Bugs / incorrect", c.bug, "rose"],
    ["Demo / seeded", c.demo, "purple"], ["Static / placeholder", c.static, "slate"],
  ];
  return (
    <div className="max-w-4xl" data-testid="docs-home">
      <p className="text-xs text-slate-400 mb-1">Docs</p>
      <h1 className="text-3xl font-bold text-slate-900">Internal documentation portal</h1>
      <p className="text-slate-500 mt-2">Private technical reference and honest state-of-play for the HTSS Ops app — every page, table and form mapped to what is actually wired to the live system, and everything mocked, seeded, static or buggy flagged. Manual audit dated {AUDIT_DATE}.</p>

      <H2>Card counts</H2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map(([label, val, col]) => (
          <div key={label} className="border border-slate-200 rounded-xl p-4">
            <div className={`text-3xl font-bold ${col === "emerald" ? "text-emerald-600" : col === "amber" ? "text-amber-600" : col === "rose" ? "text-rose-600" : col === "purple" ? "text-purple-600" : "text-slate-900"}`}>{val}</div>
            <div className="text-xs text-slate-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <H2>Status legend</H2>
      <div className="flex flex-wrap gap-2">
        {Object.keys(STATUS).map((k) => <StatusBadge key={k} status={k} />)}
      </div>

      <H2>Priority actions</H2>
      <p className="text-sm text-slate-500 mb-3">Concerns raised across all cards, sorted by severity.</p>
      <Table head={["Severity", "Card", "Concern"]} rows={concerns.map((r, i) => (
        <tr key={i} data-testid={`priority-row-${i}`}>
          <td className="py-2.5 px-3"><Sev level={r.severity} /></td>
          <td className="py-2.5 px-3 whitespace-nowrap">{r.entryId ? <Link to={`/docs/${r.entryId}`} className="text-indigo-600 hover:underline">{r.entryTitle}</Link> : <span className="text-slate-500">{r.entryTitle}</span>}</td>
          <td className="py-2.5 px-3 text-slate-700">{r.text}</td>
        </tr>
      ))} />
    </div>
  );
}

function Section({ title, children }) {
  return (<><H2>{title}</H2>{children}</>);
}

function DocsEntry() {
  const { entryId } = useParams();
  const e = entryById(entryId);
  const idx = ALL_ENTRIES.findIndex((x) => x.id === entryId);
  const next = ALL_ENTRIES[idx + 1];
  const prev = ALL_ENTRIES[idx - 1];
  if (!e) return <div className="text-slate-500">Entry not found. <Link to="/docs" className="text-indigo-600">Back to overview</Link></div>;

  const toc = [
    ["overview", "Overview"], ["sees", "What the user sees"], ["sources", "Data sources"],
    (e.howItWorks || e.calculation) && ["works", "How it works"],
    e.personalisation && ["pers", "Personalisation"], ["concerns", "Concerns"], ["code", "Code & API"],
    e.related && e.related.length && ["related", "Related"],
  ].filter(Boolean);

  return (
    <div className="flex gap-8" data-testid="docs-entry">
      <div className="flex-1 max-w-3xl min-w-0">
        <p className="text-xs text-slate-400 mb-2">Docs › {e.sectionLabel} › {e.title}</p>
        <h1 className="text-3xl font-bold text-slate-900">{e.title}</h1>
        <div className="mt-3"><StatusBadge status={e.status} /></div>
        <p className="text-xs text-slate-400 mt-2">ID: <code className="bg-slate-100 px-1 rounded">{e.id}</code> · Last updated: {e.updated} · Audit: {AUDIT_DATE}</p>

        <Section title="Overview"><span id="overview" /><p className="text-slate-700">{e.overview}</p>{e.why && <p className="text-slate-700 mt-2"><strong>Why it matters:</strong> {e.why}</p>}</Section>

        {e.userSees && <Section title="What the user sees"><span id="sees" /><ul className="list-disc pl-5 space-y-1 text-slate-700">{e.userSees.map((u, i) => <li key={i}>{u}</li>)}</ul></Section>}

        {e.dataSources && <Section title="Data sources"><span id="sources" />
          <Table head={["Source", "Kind", "Endpoint / origin", "Notes"]} rows={e.dataSources.map((ds, i) => (
            <tr key={i}><td className="py-2 px-3 font-medium">{ds.source}</td><td className="py-2 px-3"><code className="bg-slate-100 px-1 rounded text-xs">{ds.kind}</code></td><td className="py-2 px-3"><code className="bg-slate-100 px-1 rounded text-xs">{ds.endpoint}</code></td><td className="py-2 px-3 text-slate-500">{ds.notes}</td></tr>
          ))} />
        </Section>}

        {(e.howItWorks || e.calculation) && <Section title="How it works"><span id="works" />
          {e.calculation && <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700 text-sm mb-3">{e.calculation}</div>}
          {e.howItWorks && <div className="space-y-2">{e.howItWorks.map((s, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3 flex gap-3"><span className="w-6 h-6 shrink-0 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">{i + 1}</span><div><div className="font-semibold text-slate-800 text-sm">{s.title}</div><div className="text-slate-500 text-sm">{s.detail}</div></div></div>
          ))}</div>}
        </Section>}

        {e.personalisation && <Section title="Personalisation"><span id="pers" /><ul className="list-disc pl-5 text-slate-700">{e.personalisation.map((p, i) => <li key={i}>{p}</li>)}</ul></Section>}

        <Section title="Concerns"><span id="concerns" />
          {(!e.concerns || e.concerns.length === 0) ? <p className="text-slate-500">No concerns flagged at last review.</p>
            : <ul className="space-y-2">{e.concerns.map((c, i) => <li key={i} className="flex items-start gap-2"><Sev level={c.severity} /><span className="text-slate-700">{c.text}</span></li>)}</ul>}
        </Section>

        <Section title="Code & API references"><span id="code" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div><div className="text-xs font-bold uppercase text-slate-400 mb-1">Code</div><ul className="space-y-1">{(e.code || []).map((c, i) => <li key={i}><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{c}</code></li>)}</ul></div>
            <div><div className="text-xs font-bold uppercase text-slate-400 mb-1">API</div><ul className="space-y-1">{(e.api || []).map((a, i) => <li key={i}><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{a}</code></li>)}</ul></div>
          </div>
        </Section>

        {e.related && e.related.length > 0 && <Section title="Related"><span id="related" />
          <div className="flex flex-wrap gap-2">{e.related.map((r) => { const t = entryById(r); return t ? <Link key={r} to={`/docs/${r}`} className="text-sm px-2.5 py-1 rounded-md border border-slate-200 text-indigo-600 hover:bg-slate-50">{t.title}</Link> : null; })}</div>
        </Section>}

        <div className="grid grid-cols-2 gap-3 mt-10">
          <div>{prev && <Link to={`/docs/${prev.id}`} className="block border border-slate-200 rounded-lg p-3 hover:bg-slate-50"><div className="text-[10px] uppercase text-slate-400">← Prev</div><div className="text-indigo-600 text-sm">{prev.title}</div></Link>}</div>
          <div>{next && <Link to={`/docs/${next.id}`} className="block border border-slate-200 rounded-lg p-3 hover:bg-slate-50 text-right"><div className="text-[10px] uppercase text-slate-400">Next →</div><div className="text-indigo-600 text-sm">{next.title}</div></Link>}</div>
        </div>
      </div>

      <div className="w-48 shrink-0 hidden xl:block">
        <div className="sticky top-6">
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">On this page</div>
          <ul className="space-y-1.5">{toc.map(([anchor, label]) => <li key={anchor}><a href={`#${anchor}`} className="text-sm text-slate-500 hover:text-slate-900">{label}</a></li>)}</ul>
        </div>
      </div>
    </div>
  );
}

function DocsRegister() {
  const { reg } = useParams();
  if (reg === "datasource") {
    const rows = dataSourceRegister();
    return <div className="max-w-4xl" data-testid="reg-datasource"><h1 className="text-3xl font-bold text-slate-900 mb-4">Data-Source Register</h1><Table head={["Card", "Source", "Kind", "Endpoint / origin", "Notes"]} rows={rows.map((r, i) => (
      <tr key={i}><td className="py-2 px-3"><Link to={`/docs/${r.entryId}`} className="text-indigo-600 hover:underline">{r.entryTitle}</Link></td><td className="py-2 px-3 font-medium">{r.source}</td><td className="py-2 px-3"><code className="bg-slate-100 px-1 rounded text-xs">{r.kind}</code></td><td className="py-2 px-3"><code className="bg-slate-100 px-1 rounded text-xs">{r.endpoint}</code></td><td className="py-2 px-3 text-slate-500">{r.notes}</td></tr>
    ))} /></div>;
  }
  if (reg === "calculation") {
    const rows = calculationRegister();
    return <div className="max-w-4xl" data-testid="reg-calculation"><h1 className="text-3xl font-bold text-slate-900 mb-4">Calculation Register</h1><div className="space-y-4">{rows.map((r) => (
      <div key={r.entryId} className="border border-slate-200 rounded-lg p-4"><Link to={`/docs/${r.entryId}`} className="text-indigo-600 font-semibold">{r.entryTitle}</Link>{r.calculation && <p className="text-sm text-slate-700 mt-1">{r.calculation}</p>}{r.steps && <ol className="list-decimal pl-5 text-sm text-slate-600 mt-2 space-y-0.5">{r.steps.map((s, i) => <li key={i}><strong>{s.title}:</strong> {s.detail}</li>)}</ol>}</div>
    ))}</div></div>;
  }
  if (reg === "mock") {
    const rows = mockSeededRegister();
    return <div className="max-w-4xl" data-testid="reg-mock"><h1 className="text-3xl font-bold text-slate-900 mb-4">Mock &amp; Seeded Data</h1><p className="text-slate-500 mb-4">Everything below is NOT driven by the live production data path and must be wired up or replaced before go-live.</p><div className="space-y-3">{rows.map((e) => (
      <div key={e.id} className="border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3"><div><Link to={`/docs/${e.id}`} className="text-indigo-600 font-semibold">{e.title}</Link><p className="text-sm text-slate-500 mt-1">{e.overview}</p></div><StatusBadge status={e.status} /></div>
    ))}</div></div>;
  }
  if (reg === "issues") {
    const rows = allConcerns();
    return <div className="max-w-4xl" data-testid="reg-issues"><h1 className="text-3xl font-bold text-slate-900 mb-4">Known Issues</h1><Table head={["Severity", "Card", "Issue"]} rows={rows.map((r, i) => (
      <tr key={i}><td className="py-2.5 px-3"><Sev level={r.severity} /></td><td className="py-2.5 px-3 whitespace-nowrap">{r.entryId ? <Link to={`/docs/${r.entryId}`} className="text-indigo-600 hover:underline">{r.entryTitle}</Link> : r.entryTitle}</td><td className="py-2.5 px-3 text-slate-700">{r.text}</td></tr>
    ))} /></div>;
  }
  if (reg === "changelog") {
    return <div className="max-w-4xl" data-testid="reg-changelog"><h1 className="text-3xl font-bold text-slate-900 mb-4">Change Log</h1><ul className="space-y-3">{CHANGE_LOG.map((c, i) => (
      <li key={i} className="border-l-2 border-indigo-200 pl-4"><div className="text-xs text-slate-400">{c.date}</div><div className="text-slate-700">{c.text}</div></li>
    ))}</ul></div>;
  }
  return <div>Unknown register.</div>;
}

export default function DocsApp() {
  const loc = useLocation();
  // scroll to top on route change
  const key = loc.pathname;
  return (
    <div className="min-h-screen flex bg-white text-slate-900" data-testid="docs-app">
      <Sidebar />
      <main key={key} className="flex-1 min-w-0 px-6 lg:px-12 py-8 overflow-x-hidden">
        <Routes>
          <Route index element={<DocsHome />} />
          <Route path="registers/:reg" element={<DocsRegister />} />
          <Route path="feedback" element={<FeedbackAdmin />} />
          <Route path="feedback/:id" element={<FeedbackTicket />} />
          <Route path=":entryId" element={<DocsEntry />} />
        </Routes>
      </main>
    </div>
  );
}
